import { inject, injectable } from "inversify";
import maplibregl from "maplibre-gl";
import * as THREE from "three";
import { CSS2DRenderer } from "three/examples/jsm/renderers/CSS2DRenderer.js";
import { EventDispatcher, Logger } from "@designboticteam/core";
import { MapConfigService, MapLibreInstanceService } from "@designboticteam/maplibre";
import { CameraService, type IRendererService, SceneService } from "@designboticteam/workspace-3d";

const CUSTOM_LAYER_ID = "3d-model";

@injectable()
class MapLibreRendererService implements IRendererService {
	public readonly serviceName = "🗺️ MapLibreRendererService";
	private readonly logger = new Logger(`🔗 CONNECTOR | ${this.serviceName}`, {
		color: "#ffffff",
		backgroundColor: "#6644aa",
	});

	public readonly onBeforeRender = new EventDispatcher<{
		cameraProjectionMatrix: THREE.Matrix4;
		modelMatrix: THREE.Matrix4;
	}>("MapLibreRendererService.onBeforeRender");
	private readonly renderLoopListeners = new Set<() => void>();

	#renderer3D: THREE.WebGLRenderer | null = null;
	#renderer2D: CSS2DRenderer | null = null;
	public get renderer3D() {
		if (this.#renderer3D === null) {
			throw new Error("Renderer not initialized. Custom layer has not been added to the map yet.");
		}

		return this.#renderer3D;
	}

	public get renderer2D() {
		if (this.#renderer2D === null) {
			throw new Error("2D renderer not initialized. Custom layer has not been added to the map yet.");
		}

		return this.#renderer2D;
	}

	constructor(
		@inject(MapConfigService) private mapConfigService: MapConfigService,
		@inject(MapLibreInstanceService) private maplibreInstanceService: MapLibreInstanceService,
		@inject(CameraService) private cameraService: CameraService,
		@inject(SceneService) private sceneService: SceneService,
	) {
		this.logger.log("constructed");
	}

	public initialize(): void {
		const customLayer: maplibregl.CustomLayerInterface = {
			id: CUSTOM_LAYER_ID,
			type: "custom",
			renderingMode: "3d",
			onAdd: (_map, gl) => this.createRenderer({ canvas: _map.getCanvas(), context: gl }),
			render: (_gl, _options) => {
				this.renderFrame(Array.from(_options.defaultProjectionData.mainMatrix));
				this.maplibreInstanceService.map.triggerRepaint();
			},
		};

		this.maplibreInstanceService.map.on("load", () => {
			this.maplibreInstanceService.map.addLayer(customLayer);
			this.maplibreInstanceService.map.on("styledata", this.keepCustomLayerOnTop);
		});

		// this.createDebugMarkers();

		this.logger.log("initialized");
	}

	public dispose(): void {
		this.maplibreInstanceService.map.off("styledata", this.keepCustomLayerOnTop);
		this.#renderer2D?.domElement.remove();
		this.#renderer3D?.dispose();
		this.#renderer3D = null;
		this.#renderer2D = null;
	}

	/** */
	private keepCustomLayerOnTop = (): void => {
		const map = this.maplibreInstanceService.map;

		if (map.getLayer(CUSTOM_LAYER_ID) === undefined) {
			return;
		}

		const layersOrder = map.getLayersOrder();

		if (layersOrder[layersOrder.length - 1] !== CUSTOM_LAYER_ID) {
			map.moveLayer(CUSTOM_LAYER_ID);
		}
	};

	/** */
	public setPixelRatio(dpr: number): void {
		this.maplibreInstanceService.map.setPixelRatio(dpr);
		this.maplibreInstanceService.map.triggerRepaint();
	}

	public addRenderLoopListener(listener: () => void): void {
		this.renderLoopListeners.add(listener);
	}

	public removeRenderLoopListener(listener: () => void): void {
		this.renderLoopListeners.delete(listener);
	}

	private createRenderer({ canvas, context }: { canvas: HTMLCanvasElement; context: WebGLRenderingContext }): void {
		this.#renderer3D = new THREE.WebGLRenderer({
			antialias: true,
			powerPreference: "high-performance",
			canvas,
			context,
		});

		this.#renderer3D.shadowMap.enabled = true;
		this.#renderer3D.autoClear = false;

		this.#renderer2D = new CSS2DRenderer();
		Object.assign(this.#renderer2D.domElement.style, {
			position: "absolute",
			top: "0",
			left: "0",
			pointerEvents: "none",
			overflow: "hidden",
			zIndex: "1",
		} satisfies Partial<CSSStyleDeclaration>);

		const container = this.maplibreInstanceService.map.getContainer();
		container.append(this.#renderer2D.domElement);
	}

	private renderFrame(projectionMatrix: number[]): void {
		const origin = this.mapConfigService.origin;
		const mercatorCoordinate = maplibregl.MercatorCoordinate.fromLngLat(origin, 0);
		const scale = mercatorCoordinate.meterInMercatorCoordinateUnits();
		const rotationX = new THREE.Matrix4().makeRotationAxis(new THREE.Vector3(1, 0, 0), -Math.PI / 2);

		// MapLibre v5: use defaultProjectionData.mainMatrix (Mercator -> clip),
		// NOT modelViewProjectionMatrix (which is in pixel space)
		const m = new THREE.Matrix4().fromArray(projectionMatrix);
		const l = new THREE.Matrix4()
			.makeTranslation(mercatorCoordinate.x, mercatorCoordinate.y, mercatorCoordinate.z)
			.scale(new THREE.Vector3(scale, -scale, scale))
			.multiply(rotationX);

		const cameraProjectionMatrix = m.multiply(l);
		const modelMatrix = l;

		this.renderer3D.resetState();

		// Sync Three.js viewport with the canvas size managed by MapLibre (includes DPR)
		const canvas = this.maplibreInstanceService.map.getCanvas();
		const width = canvas.clientWidth;
		const height = canvas.clientHeight;
		this.renderer3D.setViewport(0, 0, this.renderer3D.getContext().canvas.width, this.renderer3D.getContext().canvas.height);
		this.renderer2D.setSize(width, height);

		// When camera is below the ground plane (y < 0), clear the current framebuffer
		// to hide MapLibre layers (map tiles, buildings) - keep only Three.js content (for ArcBallCameraControl)
		if (this.cameraService.camera.position.y < 0) {
			const gl = this.renderer3D.getContext();
			gl.disable(gl.SCISSOR_TEST);
			gl.colorMask(true, true, true, true);
			gl.depthMask(true);
			gl.stencilMask(0xff);
			gl.clearColor(0, 0, 0, 0);
			gl.clearDepth(1.0);
			gl.clearStencil(0);
			gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT | gl.STENCIL_BUFFER_BIT);
		}

		for (const listener of this.renderLoopListeners) {
			listener();
		}

		this.onBeforeRender.dispatch({ cameraProjectionMatrix, modelMatrix });
		this.sceneService.update();
		this.renderer3D.render(this.sceneService.scene, this.cameraService.camera);
		this.renderer2D.render(this.sceneService.scene, this.cameraService.camera);
	}

	// private createDebugMarkers() {
	// 	// Warsaw - Pałac kultury
	// 	// https://epsg.io/transform#s_srs=4326&t_srs=3857&x=21.0057980&y=52.2317660
	// 	// https://epsg.io/map#srs=3857&x=2338354.737066&y=6842140.694739&z=2&layer=streets
	// 	const marker = new THREE.Mesh(
	// 		new THREE.ConeGeometry(1, 2),
	// 		new THREE.MeshBasicMaterial({
	// 			color: 0xff00ff,
	// 		}),
	// 	);
	// 	marker.geometry.applyMatrix4(new THREE.Matrix4().setPosition(new THREE.Vector3(0, -1, 0)));
	// 	marker.scale.setScalar(100);

	// 	const [x, y, z] = this.maplibreInstanceService.wgs84ToMapLibreMercatorUnitsRelativeToOrigin([21.005798, 52.231766]);
	// 	marker.position.set(x, z, -y);

	// 	this.sceneService.scene.add(marker);
	// }
}

export { MapLibreRendererService };
